'use client';

import { useRef } from 'react';
import { gsap, EASE_OUT } from '@/components/sections/kit/motion';
import type { CompareDossierData } from './types';
import { CONTAINER } from './bits';
import DossierHead from './DossierHead';
import { useChoreo } from './motion';

/**
 * §07 — where the competitor wins, deliberately the heaviest surface on the
 * page (ink band). Four concessions in full, zero asterisks — don't soften,
 * don't pad (honesty rail). Light-pink eyebrow per the site's tone=light
 * convention.
 */
export default function WhereBitlyWins({ data }: { data: CompareDossierData }) {
  const rootRef = useRef<HTMLElement>(null);
  const total = data.concessions.items.length;

  useChoreo(rootRef, (root) => {
    const tl = gsap.timeline({ paused: true, defaults: { ease: EASE_OUT } });
    tl.from(root.querySelectorAll('[data-concession]'), {
      y: 24,
      opacity: 0,
      duration: 0.7,
      stagger: 0.11,
    });
    root.querySelectorAll<HTMLElement>('[data-concession-n]').forEach((el, i) => {
      tl.to(
        el,
        {
          duration: 0.7,
          scrambleText: { text: el.textContent ?? '', chars: '0123456789/', speed: 0.4 },
        },
        0.3 + i * 0.11,
      );
    });
    tl.from(root.querySelector('[data-concession-caption]'), { opacity: 0, duration: 0.45 }, '-=0.2');
    return tl;
  });

  return (
    <section ref={rootRef} className="mt-16 bg-ink md:mt-24">
      <div className={`${CONTAINER} py-14 md:py-[88px]`}>
        <DossierHead
          num="07"
          label={`WHERE ${data.competitor.toUpperCase()} WINS`}
          title={data.concessions.title}
          lead={data.concessions.lead}
          tone="light"
          leadClassName="max-w-[580px]"
        />
        <div className="mt-8 grid grid-cols-[repeat(auto-fit,minmax(260px,1fr))] gap-4 md:mt-11">
          {data.concessions.items.map((item, i) => (
            <div
              key={item.title}
              data-concession
              className="flex flex-col gap-2 rounded-ref border border-white/14 bg-white/3 px-6 py-[22px]"
            >
              <span
                data-concession-n
                className="font-jetbrains text-[10px] tracking-[0.12em] text-white/45"
              >
                CONCESSION {i + 1} / {total}
              </span>
              <h3 className="font-onest text-[16px] font-bold text-white">{item.title}</h3>
              <p className="text-[13.5px] leading-[1.6] text-pretty text-white/72">{item.body}</p>
            </div>
          ))}
        </div>
        <p
          data-concession-caption
          className="mt-5 font-jetbrains text-[10px] leading-[1.6] tracking-[0.12em] text-white/40"
        >
          {data.concessions.caption}
        </p>
      </div>
    </section>
  );
}
